import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Users, Building2, Package, ShoppingCart, FileText, Receipt, LogOut, Settings, Menu, X, Truck, Car, MapPin, UserCog, Calculator, ClipboardCheck, FolderOpen, User, ChevronDown, AlertTriangle, TrendingUp, TrendingDown, Clock, Briefcase, BarChart3, BookOpen, Monitor, Warehouse, ShieldCheck } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/utils/api';

// Prefetch map — mirrors the lazy imports in App.js
const prefetchMap = {
  '/': () => import('@/pages/Dashboard'),
  '/clients': () => import('@/pages/Clients'),
  '/sites': () => import('@/pages/Sites'),
  '/stock': () => import('@/pages/Stock'),
  '/stock-reports': () => import('@/pages/StockReports'),
  '/depots': () => import('@/pages/Depots'),
  '/suppliers': () => import('@/pages/Suppliers'),
  '/orders': () => import('@/pages/Orders'),
  '/backorders': () => import('@/pages/Backorders'),
  '/invoices': () => import('@/pages/Invoices'),
  '/invoice-leaks': () => import('@/pages/InvoiceLeaks'),
  '/quotes': () => import('@/pages/Quotes'),
  '/fleet': () => import('@/pages/Fleet'),
  '/vehicle-checks': () => import('@/pages/VehicleChecks'),
  '/assets': () => import('@/pages/Assets'),
  '/staff': () => import('@/pages/StaffNew'),
  '/attendance': () => import('@/pages/Attendance'),
  '/payroll': () => import('@/pages/Payroll'),
  '/auto-enrolment-forecast': () => import('@/pages/AutoEnrolmentForecast'),
  '/sick-pay-alerts': () => import('@/pages/SickPayAlerts'),
  '/hr': () => import('@/pages/HRPortal'),
  '/audits': () => import('@/pages/Audits'),
  '/elearning-admin': () => import('@/pages/ELearningAdmin'),
  '/documents': () => import('@/pages/Documents'),
  '/settings': () => import('@/pages/Settings'),
  '/learning-system': () => import('@/pages/LearningSystem'),
  '/profile': () => import('@/pages/Profile'),
  '/users': () => import('@/pages/UserManagement'),
};

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companyName, setCompanyName] = useState('Your Company');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  const allNavItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
    { path: '/clients', icon: Users, label: 'Clients', module: 'clients' },
    { path: '/sites', icon: Building2, label: 'Sites', module: 'sites' },
    { path: '/staff', icon: UserCog, label: 'Staff', module: 'staff' },
    { path: '/attendance', icon: Clock, label: 'Attendance', module: 'attendance' },
    { path: '/payroll', icon: Calculator, label: 'Payroll', module: 'payroll' },
    { path: '/auto-enrolment-forecast', icon: TrendingUp, label: 'AE Forecast', module: 'payroll' },
    { path: '/sick-pay-alerts', icon: AlertTriangle, label: 'Sick Pay Alerts', module: 'payroll' },
    { path: '/hr', icon: Briefcase, label: 'HR Portal', module: 'hr' },
    { path: '/audits', icon: ClipboardCheck, label: 'Audits', module: 'audits' },
    { path: '/elearning-admin', icon: BookOpen, label: 'E-Learning', module: 'elearning' },
    { path: '/orders', icon: FileText, label: 'Orders', module: 'orders' },
    { path: '/stock', icon: Package, label: 'Stock', module: 'stock' },
    { path: '/depots', icon: Warehouse, label: 'Depots', module: 'depots' },
    { path: '/suppliers', icon: Truck, label: 'Suppliers', module: 'suppliers' },
    { path: '/invoices', icon: Receipt, label: 'Invoicing', module: 'invoices' },
    { path: '/quotes', icon: FileText, label: 'Quotes', module: 'quotes' },
    { path: '/fleet', icon: Car, label: 'Fleet', module: 'fleet' },
    { path: '/assets', icon: Monitor, label: 'Assets', module: 'assets' },
    { path: '/documents', icon: FolderOpen, label: 'Documents', module: 'documents' },
    { path: '/settings', icon: Settings, label: 'Settings', module: 'settings' },
  ];

  // Filter nav items based on user permissions
  const navItems = allNavItems.filter(item => {
    // Admins and company admins see everything
    if (user?.role === 'admin' || user?.role === 'company_admin') {
      return true;
    }
    // Regular users only see what they have permission for
    return user?.permissions?.[item.module] === true;
  });

  // Prefetch page chunk on hover — webpack caches the import so navigation is instant
  const prefetch = useCallback((path) => {
    const loader = prefetchMap[path];
    if (loader) loader();
  }, []);

  // Fetch company settings and alert count
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await api.getCompanySettings();
        if (response.data?.company_name) {
          setCompanyName(response.data.company_name);
        }
      } catch (error) {
        console.error('Failed to load company settings:', error);
        // Keep default 'Your Company' on error
      }
    };
    
    const fetchAlertCount = async () => {
      try {
        const response = await api.getSickPayAlerts('active', 'critical');
        setAlertCount(response.data.length);
      } catch (error) {
        console.error('Failed to load alert count:', error);
      }
    };
    
    fetchCompanySettings();
    fetchAlertCount();
    
    // Refresh alert count every 5 minutes
    const interval = setInterval(fetchAlertCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-slate-900 text-white z-50 transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} flex flex-col`}>
        {/* Logo and Company Name */}
        <div className="p-6 flex items-center justify-between border-b border-slate-800">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              {/* Company Logo Placeholder - will be replaced with actual company logo */}
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">FT</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-white" data-testid="app-title">{companyName}</h1>
                <p className="text-slate-400 text-xs">Management Portal</p>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mx-auto">
              <span className="text-white font-bold text-lg">FT</span>
            </div>
          )}
        </div>

        {/* Toggle Button */}
        <div className="px-4 py-3 border-b border-slate-800">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full p-2 hover:bg-slate-800 rounded-lg transition-colors flex items-center justify-center"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation - Flexible grow area */}
        <nav className="px-4 py-4 space-y-1 flex-1 overflow-y-auto">
          {navItems.map(({ path, icon: Icon, label }) => {
            const isActive = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                onMouseEnter={() => prefetch(path)}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center rounded-lg transition-colors ${
                  sidebarCollapsed 
                    ? 'justify-center p-3' 
                    : 'gap-3 px-4 py-3'
                } ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                title={label}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="font-medium flex-1">{label}</span>
                )}
                {/* Alert badge for Sick Pay Alerts */}
                {path === '/sick-pay-alerts' && alertCount > 0 && (
                  <span className={`${sidebarCollapsed ? 'absolute -top-1 -right-1' : ''} flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full`}>
                    {alertCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer - Powered by FaciliTrack */}
        {!sidebarCollapsed && (
          <div className="p-4 border-t border-slate-800">
            <div className="text-center">
              <p className="text-xs text-slate-500">
                {companyName}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Powered by <span className="font-semibold text-blue-400">FaciliTrack</span>
              </p>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="flex items-center justify-between px-8 py-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900" data-testid="page-title">
                {navItems.find((item) => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              {/* User Menu Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900" data-testid="user-name">{user?.name}</p>
                    <p className="text-xs text-slate-500" data-testid="user-email">{user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
                
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    <Link
                      to="/profile"
                      onMouseEnter={() => prefetch('/profile')}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-slate-100 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4 text-slate-600" />
                      <span className="text-sm text-slate-700">Profile & Password</span>
                    </Link>
                    
                    {(user?.role === 'company_admin' || user?.role === 'admin') && (
                      <Link
                        to="/users"
                        onMouseEnter={() => prefetch('/users')}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-slate-100 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Users className="w-4 h-4 text-slate-600" />
                        <span className="text-sm text-slate-700">User Management</span>
                      </Link>
                    )}

                    {(user?.role === 'super_admin' || user?.role === 'superadmin') && (
                      <Link
                        to="/admin"
                        data-testid="nav-admin-portal"
                        className="flex items-center gap-3 px-4 py-2 hover:bg-slate-100 transition-colors border-t border-slate-200 pt-3"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">Admin Portal</span>
                      </Link>
                    )}
                    
                    <div className="border-t border-slate-200 my-2"></div>
                    
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-slate-100 transition-colors w-full text-left"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4 text-slate-600" />
                      <span className="text-sm text-slate-700">Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
